/**
 * Where to land the dispatcher after a successful build: the routes list with its
 * PERIOD set to the dates the build wrote routes on.
 *
 * Why (reviewer finding on BUG-687…693): the poller used to navigate to a bare
 * `/routes`, and `usePersistedUrlFilters` restores the last period from storage on
 * a bare landing. A dispatcher who last looked at 12–19 Oct and then built routes
 * for 21 Oct was shown «0 маршрутов / Маршруты не найдены» right under the green
 * «Маршруты построены» card. Putting the period in the URL makes the URL win over
 * the stored snapshot (and resets the other filters to their defaults, which show
 * every non-archived route), so the list shows what was just built. The snapshot
 * is then refreshed from this URL, so a later bare `/routes` keeps showing it.
 *
 * The period spans the earliest to the latest built date — an auto-build can split
 * across dates (overdue → today, future orders → their own day). With no usable
 * date (an older payload) it falls back to the bare `/routes`.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

interface DatedRoute {
  date?: string | null;
}

interface DatedGroup {
  date: string;
  route_count?: number;
}

export function builtRoutesListUrl(
  routes: readonly DatedRoute[],
  byDate: readonly DatedGroup[] = [],
): string {
  const dates = [
    ...routes.map((route) => route.date),
    ...byDate
      .filter((group) => (group.route_count ?? 1) > 0)
      .map((group) => group.date),
  ]
    .filter((date): date is string => typeof date === "string")
    .map((date) => date.slice(0, 10))
    .filter((date) => ISO_DATE.test(date))
    .sort();
  if (dates.length === 0) return "/routes";
  const params = new URLSearchParams({
    date_from: dates[0],
    date_to: dates[dates.length - 1],
  });
  return `/routes?${params.toString()}`;
}
